import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Icon } from 'components/Icons'
import { filterSidebarByRoles } from 'constants/sidebarLinks'
import { clearUser } from 'store/slices/AuthSlice'
import { recordLogout } from 'service/AuditLogoutService'
import { removeUser } from 'store/slices/UserSlice'
import useHotkey from 'hooks/useHotkey'
import styles from './CommandPalette.module.scss'

/**
 * Простое нечёткое сопоставление: возвращает score >= 0 если все символы запроса
 * найдены по порядку (без учёта регистра). Меньше = лучше.
 */
const fuzzyScore = (text, query) => {
  if (!query) return 0
  const t = String(text || '').toLowerCase()
  const q = String(query).toLowerCase()
  if (t.includes(q)) return q.length === 0 ? 0 : t.indexOf(q)
  let i = 0
  let score = 0
  let lastIdx = -1
  for (const ch of q) {
    const idx = t.indexOf(ch, i)
    if (idx === -1) return -1
    score += idx - lastIdx
    lastIdx = idx
    i = idx + 1
  }
  return 100 + score
}

const CommandPalette = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const auth = useSelector((state) => state.auth || {})
  const roles = auth.roles || []

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef(null)
  const listRef = useRef(null)

  const handleClose = () => {
    setOpen(false)
    setQuery('')
    setActiveIdx(0)
  }

  const handleLogout = async () => {
    if (!(await recordLogout())) return
    for (const key of ['token', 'refresh', 'user', 'login_roles', 'login_primary_role', 'auth_session']) localStorage.removeItem(key)
    dispatch(clearUser())
    dispatch(removeUser())
    navigate('/')
  }

  // Open on Ctrl/Cmd+K
  useHotkey(['ctrl+k', 'meta+k'], () => setOpen((o) => !o))
  // Close on Escape (when open)
  useHotkey('escape', handleClose, { enabled: open, allowInInputs: true })

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30)
    }
  }, [open])

  const navItems = useMemo(() => {
    const codes = roles.map((r) => (typeof r === 'string' ? r : r?.code))
    const items = filterSidebarByRoles(codes, { isSuper: !!auth.me?.is_superuser })
    return items.map((item) => ({
      id: `nav:${item.to}`,
      group: t('auto.cmdpal.group_nav', 'Навигация'),
      label: item.key ? t(item.key, item.text) : item.text,
      icon: 'arrow-right',
      run: () => navigate(item.to),
    }))
  }, [roles, auth.me?.is_superuser, navigate, t])

  const actionItems = useMemo(() => [
    {
      id: 'act:create-statement',
      path: '/statements/',
      group: t('auto.cmdpal.group_actions', 'Действия'),
      label: t('auto.cmdpal.create_statement', 'Создать заявление'),
      icon: 'file-text',
      run: () => navigate('/create-statement'),
    },
    {
      id: 'act:open-journal',
      path: '/journal/',
      group: t('auto.cmdpal.group_actions', 'Действия'),
      label: t('auto.cmdpal.open_journal', 'Открыть журнал'),
      icon: 'journal',
      run: () => navigate('/journal/'),
    },
    {
      id: 'act:open-schedule',
      path: '/schedule/',
      group: t('auto.cmdpal.group_actions', 'Действия'),
      label: t('auto.cmdpal.open_schedule', 'Открыть расписание'),
      icon: 'schedule',
      run: () => navigate('/schedule/'),
    },
    {
      id: 'act:logout',
      group: t('auto.cmdpal.group_actions', 'Действия'),
      label: t('auto.cmdpal.logout', 'Выйти'),
      icon: 'logout',
      run: handleLogout,
    },
  ].filter((item) => !item.path || navItems.some((nav) => nav.id === `nav:${item.path}`)), [navigate, t, navItems]) // eslint-disable-line react-hooks/exhaustive-deps

  const helpItems = useMemo(() => [
    {
      id: 'help:open',
      group: t('auto.cmdpal.group_help', 'Помощь'),
      label: t('auto.cmdpal.help_center', 'Открыть центр помощи'),
      icon: 'help-circle',
      run: () => navigate('/help/'),
    },
    {
      id: 'help:shortcuts',
      group: t('auto.cmdpal.group_help', 'Помощь'),
      label: t('auto.cmdpal.shortcuts', 'Горячие клавиши: Ctrl+K — палитра команд'),
      icon: 'info',
      run: () => undefined,
    },
  ], [navigate, t])

  const allItems = useMemo(
    () => [...navItems, ...actionItems, ...helpItems],
    [navItems, actionItems, helpItems],
  )

  const filtered = useMemo(() => {
    if (!query.trim()) return allItems
    return allItems
      .map((it) => ({ it, score: fuzzyScore(it.label, query) }))
      .filter((x) => x.score >= 0)
      .sort((a, b) => a.score - b.score)
      .map((x) => x.it)
  }, [query, allItems])

  // Group filtered preserving group key, keep original order.
  const grouped = useMemo(() => {
    const order = []
    const map = new Map()
    filtered.forEach((it) => {
      if (!map.has(it.group)) {
        map.set(it.group, [])
        order.push(it.group)
      }
      map.get(it.group).push(it)
    })
    return order.map((g) => ({ group: g, items: map.get(g) }))
  }, [filtered])

  // flat list for nav with arrow keys
  const flat = filtered

  useEffect(() => {
    if (activeIdx >= flat.length) setActiveIdx(0)
  }, [flat.length, activeIdx])

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => (i + 1) % Math.max(flat.length, 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => (i - 1 + Math.max(flat.length, 1)) % Math.max(flat.length, 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const item = flat[activeIdx]
      if (item) {
        handleClose()
        item.run?.()
      }
    }
  }

  if (!open || !auth.me) return null

  const content = (
    <div
      className={styles.overlay}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose()
      }}
      role="presentation"
    >
      <div
        className={styles.panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cmdpal-title"
      >
        <div className={styles.searchRow}>
          <Icon name="search" size={18} color="var(--color-text-muted, #888)" />
          <input
            id="cmdpal-title"
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setActiveIdx(0)
            }}
            onKeyDown={onKeyDown}
            placeholder={t('auto.cmdpal.placeholder', 'Поиск команды или страницы...')}
            className={styles.input}
            aria-label={t('auto.cmdpal.aria_search', 'Поиск команды')}
            autoComplete="off"
          />
          <kbd className={styles.kbd}>Esc</kbd>
        </div>
        <div className={styles.list} ref={listRef} role="listbox">
          {flat.length === 0 ? (
            <div className={styles.empty}>
              {t('auto.cmdpal.no_results', 'Ничего не найдено')}
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.group} className={styles.group}>
                <div className={styles.groupTitle}>{g.group}</div>
                {g.items.map((item) => {
                  const idx = flat.indexOf(item)
                  const active = idx === activeIdx
                  return (
                    <button
                      type="button"
                      key={item.id}
                      role="option"
                      aria-selected={active}
                      className={`${styles.item} ${active ? styles.itemActive : ''}`}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => {
                        handleClose()
                        item.run?.()
                      }}
                    >
                      <span className={styles.itemIcon} aria-hidden="true">
                        <Icon name={item.icon} size={16} color="currentColor" />
                      </span>
                      <span className={styles.itemLabel}>{item.label}</span>
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>
        <div className={styles.footer}>
          <span><kbd className={styles.kbd}>↑</kbd> <kbd className={styles.kbd}>↓</kbd> {t('auto.cmdpal.hint_nav', 'навигация')}</span>
          <span><kbd className={styles.kbd}>Enter</kbd> {t('auto.cmdpal.hint_select', 'выбрать')}</span>
        </div>
      </div>
    </div>
  )

  return typeof document !== 'undefined' ? createPortal(content, document.body) : content
}

export default CommandPalette
